import { capitalize, get, isEmpty } from 'lodash';
import moment from 'moment';
import { formatCurrency } from 'utils/formatCurrency';

const PACIFIC_TIMEZONE = 'America/Los_Angeles';

const CRYPTO_LOAN_CHANNEL_CODE = 'dtc-crypto';
const CRYPTO_LOAN_CHANNEL_NAME = 'DTC-CRYPTO';

const COLLATERAL_WALLET_FIELD_KEYS = [
  'walletAddress',
  'walletId',
  'destinationWallet',
  'sourceWallet',
  'toWallet',
  'fromWallet',
  'cryptoWallet',
  'verifiedWallet',
  'wallet',
];

const COLLATERAL_TRANSACTION_TYPE_LABELS = {
  initialDeposit: 'Initial Deposit',
  newDeposit: 'New Deposit',
  partialLiquidation: 'Partial Liquidation',
  fullLiquidation: 'Full Liquidation',
  withdrawal: 'Withdrawal',
  return: 'Return',
};

const WALLET_PATTERN = /\b(?:bc1|[13])[a-zA-HJ-NP-Z0-9]{25,39}\b|\b0x[a-fA-F0-9]{40}\b/gi;

export const LTV_STATUS = {
  SAFE: 'safe',
  WARNING: 'warning',
  CRITICAL: 'critical',
};

const resolveCollateralFieldValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'object') {
    const resolved = value.code ?? value.value ?? value.label ?? value.name ?? '';
    if (typeof resolved === 'object' && resolved !== null) {
      return resolveCollateralFieldValue(resolved);
    }
    return resolved;
  }

  return value;
};

export const normalizeCollateralRequiredSource = (required) => {
  if (!required) {
    return null;
  }

  if (Array.isArray(required)) {
    return { goals: required };
  }

  const wrappers = ['data', 'result', 'payload', 'response'];
  let current = required;

  wrappers.forEach((wrapper) => {
    const nested = current?.[wrapper];
    if (!nested || typeof nested !== 'object') {
      return;
    }

    if (Array.isArray(nested)) {
      current = { goals: nested };
      return;
    }

    const hasRootFields = Object.keys(current).some((key) => key.toLowerCase() !== wrapper.toLowerCase()
      && (key.toLowerCase().includes('quantity')
        || key.toLowerCase().includes('minimum')
        || key.toLowerCase().includes('suggest')
        || ['coin', 'address', 'goals'].includes(key.toLowerCase())));

    if (!hasRootFields) {
      current = nested;
    }
  });

  return current;
};

const getNestedGoalBlock = (source, key) => {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  const block = source[key] ?? source[pascalKey];

  if (block && typeof block === 'object' && !Array.isArray(block)) {
    return block;
  }

  return null;
};

const getCollateralRequiredField = (source, ...fields) => {
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return null;
  }

  const normalizedEntries = Object.entries(source).reduce((acc, [key, value]) => {
    acc[key.toLowerCase()] = value;
    return acc;
  }, {});

  const matchedField = fields.find((field) => {
    const value = normalizedEntries[field.toLowerCase()];
    if (value == null) {
      return false;
    }
    if (typeof value === 'object') {
      return false;
    }
    return String(value).trim() !== '';
  });

  return matchedField ? normalizedEntries[matchedField.toLowerCase()] : null;
};

const getScalarCollateralField = (source, ...fields) => {
  const value = getCollateralRequiredField(source, ...fields);
  if (value == null) {
    return null;
  }
  return String(value).trim();
};

const parseCollateralGoalsArray = (source) => {
  const goals = source?.goals ?? source?.Goals ?? source?.options;
  if (!Array.isArray(goals)) {
    return { minimum: null, suggested: null };
  }

  let minimum = null;
  let suggested = null;

  goals.forEach((goal) => {
    if (!goal || typeof goal !== 'object') {
      return;
    }

    const type = String(
      goal.type ?? goal.goalType ?? goal.name ?? goal.label ?? goal.code ?? ''
    ).toLowerCase();
    const quantity = getCollateralRequiredField(
      goal,
      'quantityRequired',
      'quantity',
      'amount',
      'quantityRequiredToDeposit'
    );
    const payment = getCollateralRequiredField(goal, 'paymentRequired', 'payment', 'amountUsd');

    if (!quantity) {
      return;
    }

    const entry = {
      quantity: String(quantity),
      payment: Number(payment) || 0,
    };

    if (type.includes('minimum') || type.includes('min')) {
      minimum = entry;
      return;
    }

    if (type.includes('suggest') || type.includes('recommend')) {
      suggested = entry;
    }
  });

  return { minimum, suggested };
};

/** Prefer human-readable label for values rendered in JSX. */
export const resolveCollateralDisplayValue = (value, fallback = '-') => {
  if (value == null || value === '') {
    return fallback;
  }

  if (typeof value === 'object') {
    const resolved = value.label ?? value.name ?? value.code ?? value.value ?? '';
    if (resolved === '' || resolved == null) {
      return fallback;
    }
    if (typeof resolved === 'object') {
      return resolveCollateralDisplayValue(resolved, fallback);
    }
    return String(resolved);
  }

  return String(value);
};

const isNonEmptyString = (value) => typeof value === 'string' && !isEmpty(value.trim());

export const resolveTransactionDescription = (transaction) => {
  if (transaction?.type?.id === 'charge' && isNonEmptyString(transaction.description)) {
    return transaction.description;
  }

  return resolveCollateralDisplayValue(transaction?.type);
};

const PAYMENT_RECEIVED_LABEL = 'Payment Received';
const LIQUIDATION_PROCEEDS_LABEL = 'Liquidation Proceeds';

/** Resolve the payment description label, using tags to detect liquidation payments. */
export const resolvePaymentDescription = (payment) => {
  const tags = payment?.tags;
  const hasLiquidationTag = Array.isArray(tags)
    && tags.some((tag) => typeof tag === 'string' && tag.trim().toLowerCase() === 'liquidation');

  return hasLiquidationTag ? LIQUIDATION_PROCEEDS_LABEL : PAYMENT_RECEIVED_LABEL;
};

export const toOptionalNumber = (value) => {
  if (value == null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const resolveFirstDefined = (...values) => {
  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (value != null && value !== '') {
      return value;
    }
  }

  return null;
};

export const formatOptionalUsd = (value) => {
  const amount = toOptionalNumber(value);
  if (amount == null) {
    return '-';
  }

  return `$${formatCurrency(amount, 2)}`;
};

export const formatOptionalCollateralAmount = (amount, coinType) => {
  if (amount == null || amount === '') {
    return '-';
  }

  const normalized = String(amount).trim();
  if (!normalized) {
    return '-';
  }

  if (/[a-zA-Z]{2,}/.test(normalized)) {
    return normalized;
  }

  const coin = String(coinType || '').trim();
  return coin ? `${normalized} ${coin.toUpperCase()}` : normalized;
};

const hasCollateralQuantity = (value) => value != null && value !== '';

const parseCollateralPaymentValue = (value) => {
  if (value == null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/** Setup API returns LTV as decimal (0.45 = 45%); normalize to percentage. */
export const toLtvPercentage = (ltvValue) => {
  if (ltvValue == null || ltvValue === '') {
    return null;
  }

  if (typeof ltvValue === 'object') {
    const fromCode = toLtvPercentage(ltvValue.code);
    if (fromCode != null) {
      return fromCode;
    }

    const fromLabel = toLtvPercentage(ltvValue.label);
    if (fromLabel != null) {
      return fromLabel;
    }

    const resolved = resolveCollateralFieldValue(ltvValue);
    return resolved === '' ? null : toLtvPercentage(resolved);
  }

  const num = Number(ltvValue);
  if (!Number.isFinite(num)) {
    return null;
  }

  // Decimal ratio from API (0.45 = 45%, 1.1026 = 110.26%). Whole values like 50 or 75
  // are already percentages from some setup endpoints.
  if (Math.abs(num) <= 1) {
    return num * 100;
  }

  if (num > 1 && num < 100 && num % 1 !== 0) {
    return num * 100;
  }

  return num;
};

export const formatLtvMetricDisplay = (ltvValue) => {
  const pct = toLtvPercentage(ltvValue);
  if (pct == null) {
    return '-';
  }

  // Avoid float artifacts (e.g. 0.4791 → 47.910000000000004) and trim trailing zeros.
  return `${Number.parseFloat(pct.toFixed(4))}%`;
};

export const getCollateralSummaryCurrentLtvDisplay = (summary) => {
  const currentLtvRaw = summary?.currentLtv ?? null;
  const currentLtvPct = toLtvPercentage(currentLtvRaw);

  return {
    currentLtvRaw,
    currentLtvPct,
    currentLtvDisplay: formatLtvMetricDisplay(currentLtvRaw),
  };
};

/** Contractual LTV from GET /customers/current/loans/{loanId}/setup (loanSetup.Active.LoanToValue). */
export const getContractualLtvRaw = (loanTerm) => {
  const data = loanTerm?.data;
  if (!data) {
    return null;
  }
  return get(data, 'loanToValue') || null;
};

/** Contractual (target) LTV from loan setup — decimal (0.5) or percentage (50). */
export const getTargetLtvDisplay = (loanTerm) => {
  const loanToValueRaw = getContractualLtvRaw(loanTerm);
  const targetLtvPct = toLtvPercentage(loanToValueRaw);

  return {
    loanToValueRaw,
    targetLtvPct,
    targetLtvDisplay: formatLtvMetricDisplay(loanToValueRaw),
  };
};

export const getTargetLtvTrackLabel = (loanTerm) => {
  const { targetLtvPct } = getTargetLtvDisplay(loanTerm);
  return targetLtvPct == null ? '-' : Math.round(targetLtvPct);
};

/** @deprecated Use getTargetLtvDisplay — kept for call sites that used the setup API name. */
export const getLoanTermTargetLtvDisplay = getTargetLtvDisplay;

/** True when current LTV exceeds the loan contractual LTV (normalized to percentage). */
export const checkTargetLtv = (currentLtv, loanTerm) => {
  const currentLtvPct = toLtvPercentage(currentLtv);
  const { targetLtvPct } = getTargetLtvDisplay(loanTerm);

  if (currentLtvPct == null || targetLtvPct == null) {
    return false;
  }

  return currentLtvPct > targetLtvPct;
};

/** Margin call / liquidation thresholds from GET /loans/{loanId}/setup. */
export const getLoanTermLtvThresholds = (loanTerm) => ({
  marginCallPct: toLtvPercentage(loanTerm?.data?.marginCallLtv ?? null),
  liquidationPct: toLtvPercentage(loanTerm?.data?.liquidationLtv ?? null),
});

/** LTV setup fields from collateral summary (qpay or customer summary APIs). */
export const getCollateralSummaryLtvSetup = (summary) => {
  if (!summary) {
    return { data: null };
  }

  return {
    data: {
      loanToValue: resolveFirstDefined(
        summary.loanToValue,
        summary.targetLtv,
        summary.contractualLtv,
        summary.contractualLtvPct
      ),
      marginCallLtv: resolveFirstDefined(
        summary.marginCallLtv,
        summary.marginCall,
        summary.marginCallThreshold
      ),
      liquidationLtv: resolveFirstDefined(
        summary.liquidationLtv,
        summary.liquidation,
        summary.liquidationThreshold
      ),
    },
  };
};

/** LTV tier state from setup API thresholds and contractual LTV. */
export const getDynamicLtvTierState = ({
  loanToValuePct,
  marginCallPct,
  liquidationPct,
  contractualLtvPct = null,
}) => {
  const ltv = loanToValuePct == null ? null : Math.max(0, Math.min(100, loanToValuePct));
  const marginCall = marginCallPct == null ? null : Math.max(0, Math.min(100, marginCallPct));
  const liquidation = liquidationPct == null ? null : Math.max(0, Math.min(100, liquidationPct));
  const contractual = contractualLtvPct == null
    ? null
    : Math.max(0, Math.min(100, contractualLtvPct));

  if (ltv == null || marginCall == null || liquidation == null) {
    return {
      clampedLtv: ltv ?? 0,
      marginCallThreshold: marginCall ?? 0,
      liquidationThreshold: liquidation ?? 0,
      contractualLtvThreshold: contractual ?? 0,
      isLiquidating: false,
      isCritical: false,
      isWarning: false,
      isAboveContractualLtv: false,
      isSafe: true,
    };
  }

  const liquidationThreshold = Math.max(marginCall, liquidation);
  const isLiquidating = ltv >= liquidationThreshold;
  const criticalStart = marginCall + ((liquidationThreshold - marginCall) * (2 / 3));
  const isCritical = !isLiquidating && ltv >= criticalStart;
  const isWarning = !isLiquidating && !isCritical && ltv >= marginCall;
  const isSafe = !isWarning && !isCritical && !isLiquidating;
  const isAboveContractualLtv = isSafe && contractual != null && ltv > contractual;

  return {
    clampedLtv: ltv,
    marginCallThreshold: marginCall,
    liquidationThreshold,
    contractualLtvThreshold: contractual ?? 0,
    isLiquidating,
    isCritical,
    isWarning,
    isAboveContractualLtv,
    isSafe,
  };
};

export const getCriticalLtvStart = (marginCallPct, liquidationPct) => {
  const marginCall = marginCallPct == null ? null : Math.max(0, Math.min(100, marginCallPct));
  const liquidation = liquidationPct == null ? null : Math.max(0, Math.min(100, liquidationPct));

  if (marginCall == null || liquidation == null) {
    return null;
  }

  const liquidationThreshold = Math.max(marginCall, liquidation);
  return marginCall + ((liquidationThreshold - marginCall) * (2 / 3));
};

/** LTV tier from setup API thresholds (marginCallLtv, liquidationLtv). */
export const getLtvStatus = (ltv, { marginCallPct = null, liquidationPct = null } = {}) => {
  const value = Number(ltv);

  if (Number.isNaN(value)) {
    return LTV_STATUS.SAFE;
  }

  const { isWarning, isCritical, isLiquidating } = getDynamicLtvTierState({
    loanToValuePct: value,
    marginCallPct,
    liquidationPct,
  });

  if (isLiquidating || isCritical) {
    return LTV_STATUS.CRITICAL;
  }

  if (isWarning) {
    return LTV_STATUS.WARNING;
  }

  return LTV_STATUS.SAFE;
};

const PARTIAL_LIQUIDATION_DPD_MIN = 30;
const GRACE_PERIOD_DPD_MIN = 1;
const GRACE_PERIOD_DPD_MAX = 17;

/** Payment due within grace period — late fee not yet applied (DPD > 1 and < 17). */
export const isInGracePeriod = (daysPastDue) => {
  const dpd = Number(daysPastDue) || 0;
  return dpd > GRACE_PERIOD_DPD_MIN && dpd < GRACE_PERIOD_DPD_MAX;
};

export const shouldShowPartialLiquidationBanner = ({
  isCryptoLoan,
  isCollateralLiquidated,
  daysPastDue,
  liquidationAfterDays = 45,
}) => {
  const dpd = Number(daysPastDue) || 0;
  const upperDpd = Math.min(Number(liquidationAfterDays) || 45, 45);

  return Boolean(
    isCryptoLoan
      && !isCollateralLiquidated
      && dpd >= PARTIAL_LIQUIDATION_DPD_MIN
      && dpd <= upperDpd
  );
};

/** LTV/DPD banner variant for DashboardLtvAlertPanel (spec rows 3–7). */
export const resolveDashboardLtvAlertVariant = ({
  isCryptoLoan = false,
  isCollateralLiquidated = false,
  daysPastDue = 0,
  liquidationAfterDays = 45,
  ltvPercentage = 0,
  marginCallPct = null,
  liquidationPct = null,
} = {}) => {
  if (!isCryptoLoan || isCollateralLiquidated) {
    return null;
  }

  const dpd = Number(daysPastDue) || 0;
  const upperDpd = Math.min(Number(liquidationAfterDays) || 45, 45);
  const isInPartialLiquidationWindow = dpd >= PARTIAL_LIQUIDATION_DPD_MIN && dpd <= upperDpd;

  const { isWarning, isCritical, isLiquidating } = getDynamicLtvTierState({
    loanToValuePct: ltvPercentage,
    marginCallPct,
    liquidationPct,
  });

  const isCriticalLtv = isCritical || isLiquidating;

  if (isInPartialLiquidationWindow && !isCriticalLtv && !isWarning) {
    return 'dpdPartial';
  }

  if (isCriticalLtv) {
    return 'critical';
  }

  if (isWarning) {
    return 'warning';
  }

  return null;
};

/** True when loan is a DTC crypto product (from /customers/current/loans). */
export const isDtcCryptoLoan = (loan) => {
  if (!loan) {
    return false;
  }

  const { channel } = loan;
  if (channel && typeof channel === 'object') {
    const code = String(channel.code || '').toLowerCase();
    const name = String(channel.name || '').toUpperCase();
    return code === CRYPTO_LOAN_CHANNEL_CODE || name === CRYPTO_LOAN_CHANNEL_NAME;
  }

  if (typeof channel === 'string') {
    return channel.toLowerCase() === CRYPTO_LOAN_CHANNEL_CODE;
  }

  return false;
};

/** True when the active loan is a DTC crypto product (from /customers/current/loans). */
export const resolveIsCryptoLoan = (loansList) => {
  if (!loansList?.data || !loansList.activeLoanId) {
    return false;
  }

  const activeLoan = loansList.data.find((loan) => String(loan.loanId) === String(loansList.activeLoanId)) || null;
  return isDtcCryptoLoan(activeLoan);
};

const omitCollateralWalletFields = (transaction = {}) => (
  Object.keys(transaction).reduce((acc, key) => {
    if (!COLLATERAL_WALLET_FIELD_KEYS.includes(key)) {
      acc[key] = transaction[key];
    }
    return acc;
  }, {})
);

const sanitizeCollateralDescription = (description) => {
  if (!description || typeof description !== 'string') {
    return '';
  }

  return description
    .replace(WALLET_PATTERN, '')
    .replace(/\bwallet\s*[:#]?\s*\S+/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

const sanitizeCollateralTransaction = (transaction = {}) => {
  const withoutWalletFields = omitCollateralWalletFields(transaction);

  return {
    ...withoutWalletFields,
    description: sanitizeCollateralDescription(withoutWalletFields.description),
  };
};

/** Normalize GET /customers/current/loans/:loanId/collateral (array or { summary, transactions }). */
const parseCollateralApiResponse = (data) => {
  if (!data) {
    return { summary: null, transactions: [] };
  }

  if (Array.isArray(data)) {
    return { summary: null, transactions: data };
  }

  return {
    summary: data.summary || null,
    transactions: Array.isArray(data.transactions) ? data.transactions : [],
  };
};

export const getCollateralHistoryTransactions = (collateralData) => {
  const { transactions } = parseCollateralApiResponse(collateralData);
  return transactions.map(sanitizeCollateralTransaction);
};

/** Truncated transaction ID for display: first 14 chars + .... + last 8 chars. */
const formatCollateralTransactionIdDisplay = (transactionHash) => {
  const hash = String(transactionHash || '').trim();

  if (!hash) {
    return '';
  }

  if (hash.length <= 22) {
    return hash;
  }

  return `${hash.slice(0, 14)}....${hash.slice(-8)}`;
};

/** Amount column: quantity + uppercase coin (e.g. 0.5 BTC). */
const formatCollateralHistoryAmount = (quantity, coin = 'BTC') => {
  const coinUpper = String(coin || 'BTC').toUpperCase();

  if (quantity == null || quantity === '') {
    return `0 ${coinUpper}`;
  }

  const value = Number(quantity);
  return Number.isFinite(value) ? `${value} ${coinUpper}` : `${quantity} ${coinUpper}`;
};

/** e.g. Jun 11, 2026 04:50 AM PDT */
export const formatPacificDateTimeDisplay = (dateValue) => {
  if (!dateValue) {
    return '-';
  }

  let date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    const parsed = moment(dateValue, ['MM/DD/YYYY HH:mm', 'MM/DD/YYYY h:mm A', moment.ISO_8601], true);
    if (!parsed.isValid()) {
      return '-';
    }
    date = parsed.toDate();
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TIMEZONE,
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).formatToParts(date);

  const getPart = (type) => parts.find((part) => part.type === type)?.value || '';
  return `${getPart('month')} ${getPart('day')}, ${getPart('year')} ${getPart('hour')}:${getPart('minute')} ${getPart('dayPeriod')} ${getPart('timeZoneName')}`;
};

export const formatCollateralDisplay = (collateralHeld, coinType = 'BTC') => {
  const coin = String(resolveCollateralFieldValue(coinType) || 'BTC');
  const held = String(collateralHeld ?? '').trim();

  if (!held) {
    return `0 ${coin}`;
  }

  return /[a-zA-Z]{2,}/.test(held) ? held : `${held} ${coin}`;
};

export const getCollateralSummaryHeldDisplay = (summary) => {
  if (!summary) {
    return { collateralHeld: '' };
  }

  const coin = String(resolveCollateralFieldValue(summary.coin) || 'BTC').toUpperCase();

  return {
    collateralHeld: formatCollateralHistoryAmount(summary.totalQuantity ?? summary.quantity, coin),
    asOfDate: summary.asOfDate ?? null,
  };
};

export const getCollateralRequiredDisplay = (required) => {
  const source = normalizeCollateralRequiredSource(required);
  if (!source) {
    return {
      coin: '',
      quantityRequired: '',
      minimumQuantityRequired: '',
      suggestedQuantityRequired: '',
      paymentRequired: null,
      minimumPaymentRequired: null,
      suggestedPaymentRequired: null,
      address: '',
    };
  }

  const quantityRequired = getCollateralRequiredField(source, 'quantityRequired') ?? '';
  const minQuantityRequired = getCollateralRequiredField(
    source,
    'minQuantityRequired',
    'minimumQuantityRequired',
    'minimumQuantity',
    'minQuantity',
    'quantityRequiredMinimum',
    'minimumRequiredQuantity'
  );
  const minimumBlock = getNestedGoalBlock(source, 'minimum');
  const suggestedBlock = getNestedGoalBlock(source, 'suggested');
  const goalsFromArray = parseCollateralGoalsArray(source);

  const minimumFromLegacyFields = getCollateralRequiredField(
    minimumBlock,
    'quantityRequired',
    'quantity',
    'amount'
  ) ?? goalsFromArray.minimum?.quantity
    ?? getScalarCollateralField(source, 'minimum', 'Minimum');

  const suggestedFromLegacyFields = getCollateralRequiredField(
    source,
    'suggestedQuantityRequired',
    'suggestedQuantity',
    'recommendedQuantityRequired',
    'recommendedQuantity',
    'recommendedQuantityToDeposit',
    'quantityRequiredSuggested',
    'suggestedRequiredQuantity'
  ) ?? getCollateralRequiredField(
    suggestedBlock,
    'quantityRequired',
    'quantity',
    'amount'
  ) ?? goalsFromArray.suggested?.quantity
    ?? getScalarCollateralField(source, 'suggested', 'Suggested');

  // API contract: minQuantityRequired = minimum goal, quantityRequired = suggested goal.
  let minimumQuantityRequired = '';
  let suggestedQuantityRequired = '';

  if (hasCollateralQuantity(minQuantityRequired) && hasCollateralQuantity(quantityRequired)) {
    minimumQuantityRequired = String(minQuantityRequired);
    suggestedQuantityRequired = String(quantityRequired);
  } else if (hasCollateralQuantity(minQuantityRequired)) {
    minimumQuantityRequired = String(minQuantityRequired);
    suggestedQuantityRequired = hasCollateralQuantity(suggestedFromLegacyFields)
      ? String(suggestedFromLegacyFields)
      : '';
  } else if (hasCollateralQuantity(quantityRequired)) {
    minimumQuantityRequired = hasCollateralQuantity(minimumFromLegacyFields)
      ? String(minimumFromLegacyFields)
      : String(quantityRequired);
    suggestedQuantityRequired = hasCollateralQuantity(suggestedFromLegacyFields)
      ? String(suggestedFromLegacyFields)
      : '';
  } else {
    minimumQuantityRequired = hasCollateralQuantity(minimumFromLegacyFields)
      ? String(minimumFromLegacyFields)
      : '';
    suggestedQuantityRequired = hasCollateralQuantity(suggestedFromLegacyFields)
      ? String(suggestedFromLegacyFields)
      : '';
  }

  const minPaymentRequired = getCollateralRequiredField(
    source,
    'minPaymentRequired',
    'minimumPaymentRequired'
  );
  const paymentRequiredRaw = getCollateralRequiredField(source, 'paymentRequired');
  const paymentRequired = parseCollateralPaymentValue(paymentRequiredRaw);
  const suggestedPaymentParsed = parseCollateralPaymentValue(
    getCollateralRequiredField(source, 'suggestedPaymentRequired')
  );
  const minPaymentParsed = parseCollateralPaymentValue(minPaymentRequired);

  let minimumPaymentRequired = null;
  let suggestedPaymentRequired = null;

  if (minPaymentParsed != null && paymentRequired != null) {
    minimumPaymentRequired = minPaymentParsed;
    suggestedPaymentRequired = paymentRequired;
  } else if (minPaymentParsed != null) {
    minimumPaymentRequired = minPaymentParsed;
    suggestedPaymentRequired = suggestedPaymentParsed;
  } else if (paymentRequired != null) {
    minimumPaymentRequired = paymentRequired;
    suggestedPaymentRequired = suggestedPaymentParsed ?? paymentRequired;
  } else {
    suggestedPaymentRequired = suggestedPaymentParsed;
  }

  const address = getCollateralRequiredField(
    source,
    'address',
    'walletAddress',
    'depositAddress',
    'wallet'
  );

  return {
    coin: String(resolveCollateralFieldValue(source.coin) || ''),
    quantityRequired: String(quantityRequired),
    minimumQuantityRequired: String(minimumQuantityRequired),
    suggestedQuantityRequired: String(suggestedQuantityRequired),
    paymentRequired,
    minimumPaymentRequired,
    suggestedPaymentRequired,
    address: address == null ? '' : String(address),
  };
};

export const buildCollateralGoalOptionsFromDisplay = (
  display,
  coinType,
  { ensureBothCards = false, targetLtvTrackLabel = '-' } = {}
) => {
  const resolvedDisplay = display || {};
  const coin = String(coinType || resolvedDisplay.coin || '').toUpperCase();
  const minimumAmount = resolvedDisplay.minimumQuantityRequired || '';
  const suggestedAmount = resolvedDisplay.suggestedQuantityRequired || '';
  const suggestedDescription = targetLtvTrackLabel === '-'
    ? 'Returns to your contract LTV'
    : `Returns to your contract LTV of ${targetLtvTrackLabel}%`;

  const buildMinimumOption = (amount) => ({
    key: 'minimum',
    label: 'Minimum',
    amount: hasCollateralQuantity(amount) ? String(amount) : '',
    displayAmount: formatOptionalCollateralAmount(amount, coin),
    description: 'Stops your margin call',
    paymentAmount: resolvedDisplay.minimumPaymentRequired,
  });

  const buildSuggestedOption = (amount) => ({
    key: 'suggested',
    label: 'Suggested',
    amount: hasCollateralQuantity(amount) ? String(amount) : '',
    displayAmount: formatOptionalCollateralAmount(amount, coin),
    description: suggestedDescription,
    paymentAmount: resolveFirstDefined(
      resolvedDisplay.paymentRequired,
      resolvedDisplay.suggestedPaymentRequired
    ),
  });

  if (ensureBothCards) {
    return [
      buildMinimumOption(minimumAmount),
      buildSuggestedOption(suggestedAmount || minimumAmount),
    ];
  }

  const options = [];

  if (minimumAmount) {
    options.push(buildMinimumOption(minimumAmount));
  }

  if (suggestedAmount && suggestedAmount !== minimumAmount) {
    options.push(buildSuggestedOption(suggestedAmount));
  }

  return options;
};

export const buildCollateralGoalOptions = (required, coinType) => {
  if (!required) {
    return [];
  }

  const display = getCollateralRequiredDisplay(required);
  return buildCollateralGoalOptionsFromDisplay(display, coinType);
};

export const mapCollateralHistoryRows = (transactions = [], coinType = 'BTC') => (
  [...transactions]
    .map(sanitizeCollateralTransaction)
    .sort((a, b) => {
      const dateA = a.transactionDate || a.date;
      const dateB = b.transactionDate || b.date;
      return new Date(dateB) - new Date(dateA);
    })
    .map((transaction) => {
      const transactionDate = transaction.transactionDate || transaction.date;
      const transferTypeRaw = transaction.transferType || transaction.type;
      const transferType = resolveCollateralFieldValue(transferTypeRaw);
      const coin = String(resolveCollateralFieldValue(transaction.coin) || coinType).toUpperCase();
      const quantity = transaction.quantity ?? transaction.units ?? '';
      const statusRaw = transaction.status;
      const statusLabel = resolveCollateralDisplayValue(
        transaction.statusLabel || statusRaw,
        '-'
      );
      const transactionHash = transaction.transactionHash || transaction.hash || '';
      const typeLabel = (() => {
        const resolved = resolveCollateralDisplayValue(
          transaction.typeLabel || transferTypeRaw,
          ''
        );
        if (resolved) {
          return capitalize(String(resolved));
        }
        return COLLATERAL_TRANSACTION_TYPE_LABELS[transferType]
          || (isEmpty(transferType) ? '-' : capitalize(String(transferType)));
      })();

      return {
        date: transactionDate,
        transferType,
        typeLabel,
        amountDisplay: formatCollateralHistoryAmount(quantity, coin),
        statusLabel,
        transactionHash,
        transactionIdDisplay: formatCollateralTransactionIdDisplay(transactionHash),
      };
    })
);

const getLiquidationHistoryList = (liquidationsData) => {
  if (Array.isArray(liquidationsData)) {
    return liquidationsData;
  }

  if (Array.isArray(liquidationsData?.data)) {
    return liquidationsData.data;
  }

  if (Array.isArray(liquidationsData?.liquidations)) {
    return liquidationsData.liquidations;
  }

  return [];
};

const formatOptionalText = (value) => {
  if (value == null || value === '') {
    return '-';
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    const label = resolveFirstDefined(
      value.label,
      value.name,
      value.description,
      value.text,
      value.value,
      value.code
    );

    if (label == null || typeof label === 'object') {
      return '-';
    }

    const labelText = String(label).trim();
    return labelText || '-';
  }

  const text = String(value).trim();
  return text ? capitalize(text) : '-';
};

const pickLiquidationExecPrice = (order, item) => {
  const averagePrice = toOptionalNumber(resolveFirstDefined(order.averagePrice, item.averagePrice));
  const listedPrice = toOptionalNumber(resolveFirstDefined(order.price, item.price));

  if (averagePrice != null && averagePrice !== 0) {
    return averagePrice;
  }

  return listedPrice;
};

export const mapLiquidationHistoryRows = (liquidationsData, coinType = 'BTC') => (
  getLiquidationHistoryList(liquidationsData).map((item, index) => {
    const order = item?.order && typeof item.order === 'object' ? item.order : {};
    const coin = String(item.coin || coinType).toUpperCase();

    return {
      id: resolveFirstDefined(order.orderId, item.orderId, item.clientOrderId, index),
      dateTime: item.createdOn ? moment(item.createdOn).format('MM/DD/YYYY hh:mm A') : '-',
      liquidationType: formatOptionalText(item.liquidationType),
      status: formatOptionalText(resolveFirstDefined(order.status, item.status)),
      reason: formatOptionalText(resolveFirstDefined(order.reason, item.reason)),
      ltvAtTrigger: formatLtvMetricDisplay(item.ltv),
      collateralSold: formatOptionalCollateralAmount(
        resolveFirstDefined(order.quantity, item.quantity, order.filledQuantity, item.filledQuantity),
        coin
      ),
      execPrice: formatOptionalUsd(pickLiquidationExecPrice(order, item)),
      proceeds: formatOptionalUsd(resolveFirstDefined(order.netProceeds, item.netProceeds)),
    };
  })
);

/** Collateral action button label from current LTV vs contractual / margin call thresholds. */
export const getCollateralActionLabel = ({
  currentLtvPct = null,
  marginCallPct = null,
  contractualLtvPct = null,
} = {}) => {
  const isAboveMarginCall = marginCallPct != null
    && currentLtvPct != null
    && currentLtvPct >= marginCallPct;
  const isAboveContractual = contractualLtvPct != null
    && currentLtvPct != null
    && currentLtvPct > contractualLtvPct;

  if (isAboveMarginCall) {
    return 'Resolve Liquidation';
  }

  if (isAboveContractual) {
    return 'Protect Your Collateral';
  }

  return null;
};

/** Welcome header copy for crypto dashboard (single or combined LTV + past-due states).  */
export const getCryptoWelcomeHeaderContent = ({
  showCryptoPastDueStatus = false,
  isLtvCriticalWarning = false,
  isLtvMarginCall = false,
  isLtvAboveContractual = false,
  daysPastDue = 0,
  currentLtvDisplay = '-',
  liquidationAfterDays = 45,
  maturityWarning = null,
  // currentLtvPct = null,
  // marginCallPct = null,
  // contractualLtvPct = null,
} = {}) => {
  // const hasLtvIssue = isLtvCriticalWarning || isLtvMarginCall || isLtvAboveContractual;

  // Highest priority: loan matured (red)
  if (maturityWarning && (maturityWarning.variant === 'maturedGrace' || maturityWarning.variant === 'matured')) {
    return {
      icon: 'danger',
      title: 'Pay now to avoid liquidation',
      subtitle: `You must pay off your loan by ${maturityWarning.graceDeadlineDate}. If not paid off by this date, your collateral may be fully or partially liquidated.`,
    };
  }

  // Near maturity: within 30 days (blue)
  if (maturityWarning && maturityWarning.variant === 'nearMaturity') {
    return {
      icon: 'info',
      title: `Your full balance is due on ${maturityWarning.fullAmountDueDate}`,
      subtitle: `If not paid off by ${maturityWarning.fullAmountDueDate}, your collateral may be fully or partially liquidated to satisfy the total amount due.`,
    };
  }

  if (showCryptoPastDueStatus && isLtvCriticalWarning) {
    const dpd = Number(daysPastDue) || 0;

    return {
      icon: 'critical',
      title: 'Take action now to resolve delinquency and liquidation',
      subtitle: 'Click "Resolve Liquidation" to make payment or add collateral.',
      subtitleLine2: `Your account is ${dpd} days past due and LTV is ${currentLtvDisplay}.`,
    };
  }

  if (showCryptoPastDueStatus && isLtvAboveContractual) {
    const dpd = Number(daysPastDue) || 0;
    const liqDay = Number(liquidationAfterDays) || 45;
    const daysLeft = Math.max(0, liqDay - dpd);

    return {
      icon: 'warning',
      title: 'Take action now to resolve delinquency',
      subtitle: `Click "Make a Payment" to resolve delinquency and avoid potential liquidation in ${daysLeft} days.`,
      subtitleLine2: `Your account is ${dpd} days past due.`,
    };
  }

  // if (showCryptoPastDueStatus && hasLtvIssue) {
  //   const collateralCta = getCollateralActionLabel({
  //     currentLtvPct,
  //     marginCallPct,
  //     contractualLtvPct,
  //   });

  //   if (collateralCta) {
  //     return {
  //       icon: 'pastdue',
  //       title: 'Resolve your delinquency and collateral needs',
  //       subtitle: `Use the Make a Payment and ${collateralCta} buttons to address these`,
  //     };
  //   }
  // }

  if (isLtvCriticalWarning || isLtvMarginCall) {
    return {
      icon: 'critical',
      title: 'Take action now to resolve liquidation',
      subtitle: 'Click "Resolve Liquidation" to add collateral or make an extra payment.',
    };
  }

  if (isLtvAboveContractual) {
    return {
      icon: 'warning',
      title: 'Your LTV is above your target',
      subtitle: 'Click "Protect Your Collateral" to add collateral or make an extra payment to prevent a margin call.',
    };
  }

  if (showCryptoPastDueStatus) {
    return {
      icon: 'pastdue',
      title: 'Your payment is past due.',
      subtitle: 'Use the "Make a Payment" button to bring your account current.',
    };
  }

  return null;
};

export const resolveDashboardCryptoState = ({
  isCrypto,
  loanDetail,
  loanTerm,
  collateralSummary,
  collateralRequired,
}) => {
  const pastDueRaw = Number(get(loanDetail, 'data.0.daysPastDue', 0));
  const pastDue = Number.isNaN(pastDueRaw) ? 0 : pastDueRaw;
  const showCryptoPastDueStatus = pastDue > 0;
  const showGracePeriod = isInGracePeriod(pastDue);
  const statusDueFee = Number(get(loanDetail, 'data.0.dueFee', 0)) || 0;
  const currentLtvRaw = get(collateralSummary, 'data.currentLtv');
  const currentLtvPct = toLtvPercentage(currentLtvRaw);
  const { marginCallPct, liquidationPct } = getLoanTermLtvThresholds(loanTerm);
  const { targetLtvPct: contractualLtvPct, targetLtvDisplay: contractualLtvDisplay } = getTargetLtvDisplay(loanTerm);
  const isCollateralLiquidated = Boolean(get(loanDetail, 'data.0.isCollateralLiquidated', false));
  const liquidationAfterDays = Number(get(loanDetail, 'data.0.collateralLiquidationDaysPastDue', 45)) || 45;
  const clampedLtvValue = Math.max(0, Math.min(100, toLtvPercentage(currentLtvRaw) ?? 0));
  const {
    isAboveContractualLtv: isLtvAboveContractual,
    isWarning: isLtvMarginCall,
    isCritical: isLtvCritical,
    isLiquidating: isLtvLiquidating,
  } = getDynamicLtvTierState({
    loanToValuePct: clampedLtvValue,
    marginCallPct,
    liquidationPct,
    contractualLtvPct,
  });
  const ltvStatus = getLtvStatus(clampedLtvValue, { marginCallPct, liquidationPct });
  const isLtvWarning = ltvStatus === LTV_STATUS.WARNING;
  const isLtvCriticalWarning = ltvStatus === LTV_STATUS.CRITICAL;
  const showDpdPartialLiquidationBanner = isCrypto && shouldShowPartialLiquidationBanner({
    isCryptoLoan: true,
    isCollateralLiquidated,
    daysPastDue: pastDue,
    liquidationAfterDays,
  });
  const showAccountStatus = !showCryptoPastDueStatus
    && !isLtvAboveContractual
    && !isLtvWarning
    && !isLtvCriticalWarning;
  const collateralHeldDisplay = getCollateralSummaryHeldDisplay(collateralSummary?.data).collateralHeld || '-';
  const collateralRequiredDisplay = getCollateralRequiredDisplay(collateralRequired?.data);
  const lowerLtvData = {
    currentLtv: currentLtvRaw,
    contractualLtvDisplay,
    contractualLtvPct,
    collateralHeld: collateralHeldDisplay,
    suggestedPaymentAmount: toOptionalNumber(get(loanDetail, 'data.0.suggestedPaymentAmount')),
    marginCallThreshold: marginCallPct == null ? null : Math.round(marginCallPct),
    liquidationThreshold: liquidationPct == null ? null : Math.round(liquidationPct),
    addCollateralBtc: resolveFirstDefined(
      collateralRequiredDisplay.minimumQuantityRequired,
      collateralRequiredDisplay.quantityRequired
    ),
    payAmountUsd: resolveFirstDefined(
      collateralRequiredDisplay.paymentRequired,
      collateralRequiredDisplay.minimumPaymentRequired
    ),
    coinType: resolveFirstDefined(
      collateralRequiredDisplay.coin,
      get(loanDetail, 'data.0.coinType')
    ),
    address: collateralRequiredDisplay.address,
    daysPastDue: pastDue,
    isCollateralLiquidated,
    liquidationAfterDays,
  };

  return {
    pastDue,
    showCryptoPastDueStatus,
    showGracePeriod,
    statusDueFee,
    marginCallPct,
    liquidationPct,
    isCollateralLiquidated,
    liquidationAfterDays,
    clampedLtvValue,
    currentLtvPct,
    contractualLtvPct,
    contractualLtvDisplay,
    isLtvAboveContractual,
    isLtvMarginCall,
    isLtvCritical,
    isLtvLiquidating,
    isLtvWarning,
    isLtvCriticalWarning,
    showDpdPartialLiquidationBanner,
    showAccountStatus,
    lowerLtvData,
  };
};

// --- Deficiency / Liquidation Utilities ---

const toTagLabel = (tag) => {
  if (tag == null || tag === '') {
    return '';
  }

  if (typeof tag === 'string' || typeof tag === 'number') {
    return String(tag);
  }

  if (typeof tag === 'object') {
    return [tag.label, tag.name, tag.code, tag.value, tag.tag, tag.type]
      .filter((part) => part != null && part !== '' && typeof part !== 'object')
      .join(' ');
  }

  return '';
};

const normalizeTagLabel = (tag) => toTagLabel(tag)
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .toLowerCase()
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const isDeficiencyCollectionTag = (tag) => {
  const normalized = normalizeTagLabel(tag);
  if (!normalized) {
    return false;
  }

  return normalized.includes('deficiency collection')
    || normalized.replace(/\s/g, '').includes('deficiencycollection');
};

const hasDeficiencyCollectionTag = (loanDetail) => {
  const tags = get(loanDetail, 'tags');
  return Array.isArray(tags) && tags.some(isDeficiencyCollectionTag);
};

/**
 * Determines if a loan qualifies as a Deficiency Balance loan.
 * The status payload marks this with a Deficiency Collection tag
 * (name/label such as DeficiencyCollection). Boolean flags are also honored
 * when the API sends them. An outstanding principal balance is required.
 *
 * @param {Object} loanDetail - The loan detail object (data[0] from API response)
 * @returns {boolean}
 */
export const isDeficiencyBalanceLoan = (loanDetail) => {
  if (!loanDetail || typeof loanDetail !== 'object') {
    return false;
  }

  const isChargedOff = get(loanDetail, 'isChargedOff', false);
  const isDeficiencyCollection = get(loanDetail, 'isDeficiencyCollection', false) === true
    || hasDeficiencyCollectionTag(loanDetail);
  const principalBalance = Number(get(loanDetail, 'principalBalance', 0));

  return (
    isChargedOff && (isDeficiencyCollection && principalBalance > 0)
  );
};

/**
 * Determines if a loan qualifies as Liquidation Satisfied.
 * The account is considered satisfied when collateral has been liquidated
 * and the liquidation proceeds were sufficient to cover the balance
 * (i.e. Principal Balance <= 0).
 *
 * Priority: If the loan is liquidated:
 *   - Principal Balance <= 0 → Liquidation Satisfied
 *   - Principal Balance > 0  → Deficiency Balance
 *
 * @param {Object} loanDetail - The loan detail object (data[0] from API response)
 * @returns {boolean}
 */
export const isLiquidationSatisfiedLoan = (loanDetail) => {
  if (!loanDetail || typeof loanDetail !== 'object') {
    return false;
  }

  const isCollateralLiquidated = get(loanDetail, 'isCollateralLiquidated', false);
  const principalBalance = Number(get(loanDetail, 'principalBalance', 0));

  return isCollateralLiquidated === true && principalBalance <= 0;
};

const isPendingPayoffTag = (tag) => {
  const normalized = normalizeTagLabel(tag);
  if (!normalized) {
    return false;
  }

  return normalized.includes('pending payoff')
    || normalized.replace(/\s/g, '').includes('pendingpayoff');
};

/**
 * True only when the status `tags` array includes Pending Payoff.
 * Other tags do not count. Deficiency collection, then liquidation, then
 * pending payoff priority is applied by the caller, not here.
 *
 * @param {Object} loanDetail - The loan status object (data[0] from the status API)
 * @returns {boolean}
 */
export const isPendingPayoffLoan = (loanDetail) => {
  if (!loanDetail || typeof loanDetail !== 'object') {
    return false;
  }

  const tags = get(loanDetail, 'tags');
  if (!Array.isArray(tags) || tags.length === 0) {
    return false;
  }

  return tags.some(isPendingPayoffTag);
};

/**
 * Extracts liquidation summary data from the loan detail response.
 *
 * @param {Object} loanDetail - The loan detail object (data[0] from API response)
 * @returns {Object|null} - { liquidationDate, collateralLiquidated, assetType, liquidationProceeds }
 */
export const getLiquidationSummary = (loanDetail) => {
  if (!loanDetail || typeof loanDetail !== 'object') {
    return null;
  }

  const summary = get(loanDetail, 'liquidationSummary', null);

  if (!summary || typeof summary !== 'object') {
    return null;
  }

  return {
    liquidationDate: get(summary, 'liquidationDate', null),
    collateralLiquidated: get(summary, 'collateralLiquidated', null),
    assetType: get(summary, 'assetType', null),
    liquidationProceeds: get(summary, 'liquidationProceeds', null),
  };
};

/**
 * Formats the collateral amount with asset denomination.
 * e.g. formatCollateralAmount(0.45, 'BTC') → "0.4500 BTC"
 *
 * @param {number|string} amount - The collateral amount
 * @param {string} assetType - The asset denomination (e.g., "BTC")
 * @returns {string} - Formatted string (e.g., "0.4500 BTC")
 */
export const formatCollateralAmount = (amount, assetType) => {
  if (amount == null || amount === '') {
    return '-';
  }

  // The value may already include a unit suffix (e.g. "0.61 BTC").
  // Extract the leading numeric portion and any trailing asset token.
  const rawValue = String(amount).trim();
  const numericMatch = rawValue.match(/-?\d*\.?\d+/);
  if (!numericMatch) {
    return '-';
  }

  const numericAmount = Number(numericMatch[0]);
  if (!Number.isFinite(numericAmount)) {
    return '-';
  }

  const formattedAmount = numericAmount.toFixed(4);
  const suffixToken = rawValue.slice(numericMatch.index + numericMatch[0].length).trim();
  const asset = String(assetType || suffixToken || '').trim().toUpperCase();

  return asset ? `${formattedAmount} ${asset}` : formattedAmount;
};

/**
 * Formats a date as MM/DD/YYYY.
 * Returns '-' for invalid or missing dates.
 *
 * @param {string|Date} date - The date value
 * @returns {string} - Formatted date string (MM/DD/YYYY) or '-'
 */
export const formatLiquidationDate = (date) => {
  if (date == null || date === '') {
    return '-';
  }

  const parsed = typeof date === 'string'
    ? moment(date.trim(), ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true)
    : moment(date);

  if (!parsed.isValid()) {
    return '-';
  }

  return parsed.format('MM/DD/YYYY');
};
